import {
  INPUT_BITS,
  PROTOCOL_VERSION,
  isServerMessage,
  type ServerMessage,
} from "@tanks/protocol";

export type ConnectionStatus =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "unavailable";

interface GameSocketOptions {
  token: string;
  onMessage: (message: ServerMessage) => void;
  onStatus: (status: ConnectionStatus) => void;
}

export class GameSocket {
  private readonly token: string;
  private readonly onMessage: (message: ServerMessage) => void;
  private readonly onStatus: (status: ConnectionStatus) => void;
  private socket: WebSocket | null = null;
  private retryTimer: number | null = null;
  private pingTimer: number | null = null;
  private stopped = false;
  private retryCount = 0;
  private sequence = 0;
  private movement = 0;
  private axisX = 0;
  private axisY = 0;
  private inputTimer: number | null = null;
  private pendingFire = false;
  private lastInputSentAt = 0;
  private readonly inputIntervalMs = 50;

  constructor(options: GameSocketOptions) {
    this.token = options.token;
    this.onMessage = options.onMessage;
    this.onStatus = options.onStatus;
  }

  connect(): void {
    if (this.stopped) return;
    this.onStatus(this.retryCount === 0 ? "connecting" : "reconnecting");
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(
      `${protocol}//${window.location.host}/api/games/${this.token}/socket`,
    );
    this.socket = socket;

    socket.addEventListener("open", () => {
      this.retryCount = 0;
      this.onStatus("connected");
      this.sendInput(this.movement, false, this.axisX, this.axisY);
      this.startPings();
    });
    socket.addEventListener("message", (event) => {
      if (typeof event.data !== "string") return;
      let value: unknown;
      try {
        value = JSON.parse(event.data);
      } catch {
        return;
      }
      if (isServerMessage(value)) this.onMessage(value);
    });
    socket.addEventListener("close", () => this.handleClose(socket));
    socket.addEventListener("error", () => {
      if (socket.readyState < WebSocket.CLOSING) socket.close();
    });
  }

  private handleClose(socket: WebSocket): void {
    if (this.socket !== socket || this.stopped) return;
    this.stopPings();
    this.socket = null;
    if (this.inputTimer !== null) window.clearTimeout(this.inputTimer);
    this.inputTimer = null;
    this.pendingFire = false;
    // A reconnect must never replay the last held joystick direction. The
    // server also leases input, but clearing locally prevents a stale input
    // from being sent as soon as the replacement socket opens.
    this.movement = 0;
    this.axisX = 0;
    this.axisY = 0;
    this.retryCount += 1;
    if (this.retryCount >= 8) {
      this.onStatus("unavailable");
      return;
    }
    this.onStatus("reconnecting");
    const delay = Math.min(8_000, 400 * 2 ** (this.retryCount - 1));
    const jitter = Math.floor(Math.random() * 250);
    this.retryTimer = window.setTimeout(() => this.connect(), delay + jitter);
  }

  private send(value: object): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(value));
  }

  private flushInput(): void {
    if (this.inputTimer !== null) window.clearTimeout(this.inputTimer);
    this.inputTimer = null;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.sequence += 1;
    this.send({
      v: PROTOCOL_VERSION,
      type: "input",
      sequence: this.sequence,
      movement: this.movement,
      axisX: this.axisX,
      axisY: this.axisY,
      fire: this.pendingFire,
    });
    this.pendingFire = false;
    this.lastInputSentAt = performance.now();
  }

  sendInput(movement: number, fire: boolean, axisX = this.axisX, axisY = this.axisY): void {
    this.movement = movement;
    this.axisX = axisX;
    this.axisY = axisY;
    this.pendingFire ||= fire;
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    const elapsed = performance.now() - this.lastInputSentAt;
    // Releases and shots are immediate; continuous joystick updates are
    // coalesced to 20 messages/second, below the server's rate limit.
    if (fire || movement === 0 || elapsed >= this.inputIntervalMs) {
      this.flushInput();
      return;
    }
    if (this.inputTimer === null) {
      this.inputTimer = window.setTimeout(
        () => this.flushInput(),
        Math.max(0, this.inputIntervalMs - elapsed),
      );
    }
  }

  sendRematch(ready: boolean): void {
    this.send({
      v: PROTOCOL_VERSION,
      type: "rematch",
      ready,
    });
  }

  private startPings(): void {
    this.stopPings();
    this.pingTimer = window.setInterval(() => {
      this.send({
        v: PROTOCOL_VERSION,
        type: "ping",
        sentAt: Date.now(),
      });
    }, 3_000);
  }

  private stopPings(): void {
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  close(): void {
    this.stopped = true;
    this.stopPings();
    if (this.retryTimer !== null) window.clearTimeout(this.retryTimer);
    if (this.inputTimer !== null) window.clearTimeout(this.inputTimer);
    this.inputTimer = null;
    this.pendingFire = false;
    this.movement = 0;
    this.axisX = 0;
    this.axisY = 0;
    this.socket?.close(1000, "Page closed");
    this.socket = null;
  }
}

export function movementMask(keys: ReadonlySet<string>): number {
  let mask = 0;
  if (keys.has("ArrowUp") || keys.has("KeyW")) mask |= INPUT_BITS.forward;
  if (keys.has("ArrowDown") || keys.has("KeyS")) mask |= INPUT_BITS.backward;
  if (keys.has("ArrowLeft") || keys.has("KeyA")) mask |= INPUT_BITS.left;
  if (keys.has("ArrowRight") || keys.has("KeyD")) mask |= INPUT_BITS.right;
  return mask;
}

export function movementAxes(movement: number): { axisX: number; axisY: number } {
  return {
    axisX: Number((movement & INPUT_BITS.right) !== 0) -
      Number((movement & INPUT_BITS.left) !== 0),
    axisY: Number((movement & INPUT_BITS.backward) !== 0) -
      Number((movement & INPUT_BITS.forward) !== 0),
  };
}
